import React, { useState, useEffect, ChangeEvent } from 'react';
import { api } from '../../services/api';
import { FormField, Input, TextArea } from '../../components/common/FormField';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { useToast } from '../../hooks/useToast';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isAdmin: boolean;
  avatar?: string;
  bio?: string;
}

interface SocialLinks {
  facebook?: string;
  twitter?: string;
  instagram?: string;
}

interface SiteConfig {
  siteName: string;
  description: string;
  contactEmail: string;
  socialLinks: SocialLinks;
}

interface EmailConfig {
  fromEmail: string;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
}

interface PaymentConfig {
  currency: string;
}

interface TradingCardConfig {
  defaultStyle: string;
  defaultFontFamily: string;
  defaultBackgroundColor: string;
  defaultTextColor: string;
}

interface PromoModalConfig {
  enabled: boolean;
  /** Changing this resets “dismissed” for visitors (new localStorage key). */
  campaignKey: string;
  title: string;
  imageUrl: string;
  startsAt: string | null;
  endsAt: string | null;
}

interface AppSettings {
  siteConfig: SiteConfig;
  emailConfig: EmailConfig;
  paymentConfig: PaymentConfig;
  tradingCardConfig: TradingCardConfig;
  promoModalConfig: PromoModalConfig;
}

const defaultSettings: AppSettings = {
  siteConfig: {
    siteName: 'RaceCarly',
    description: 'Your premier destination for racing collectibles and memorabilia',
    contactEmail: 'support@racecarly.com',
    socialLinks: {
      facebook: 'https://facebook.com/racecarly',
      twitter: 'https://twitter.com/racecarly',
      instagram: 'https://instagram.com/racecarly',
    },
  },
  emailConfig: {
    fromEmail: 'noreply@racecarly.com',
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: 'noreply@racecarly.com',
    smtpPass: '',
  },
  paymentConfig: {
    currency: 'USD',
  },
  tradingCardConfig: {
    defaultStyle: 'modern',
    defaultFontFamily: 'Helvetica Neue',
    defaultBackgroundColor: '#1a1a1a',
    defaultTextColor: '#ffffff',
  },
  promoModalConfig: {
    enabled: false,
    campaignKey: 'bborr-2026',
    title: 'Big Bend Open Road Race',
    imageUrl: '/promo-bborr-flyer.png',
    startsAt: null,
    endsAt: null,
  },
};

function toDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(v: string): string | null {
  const t = v.trim();
  if (!t) return null;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function normalizeSettingsRow(raw: unknown): AppSettings {
  const row =
    raw && typeof raw === 'object' && raw !== null && 'data' in raw && (raw as { data: unknown }).data != null
      ? (raw as { data: Partial<AppSettings> }).data
      : (raw as Partial<AppSettings> | null);

  const s = row && typeof row === 'object' ? row : {};

  return {
    siteConfig: { ...defaultSettings.siteConfig, ...s.siteConfig },
    emailConfig: { ...defaultSettings.emailConfig, ...s.emailConfig },
    paymentConfig: { ...defaultSettings.paymentConfig, ...s.paymentConfig },
    tradingCardConfig: { ...defaultSettings.tradingCardConfig, ...s.tradingCardConfig },
    promoModalConfig: { ...defaultSettings.promoModalConfig, ...s.promoModalConfig },
  };
}

function computePromoVisitorPreview(cfg: PromoModalConfig): { showing: boolean; detail: string } {
  if (!cfg.enabled) return { showing: false, detail: 'Promo is turned off.' };
  if (!cfg.imageUrl?.trim()) return { showing: false, detail: 'Add an image URL (e.g. /promo-bborr-flyer.png).' };
  const now = Date.now();
  if (cfg.startsAt) {
    const t = new Date(cfg.startsAt).getTime();
    if (Number.isFinite(t) && now < t) return { showing: false, detail: 'Start time is in the future.' };
  }
  if (cfg.endsAt) {
    const t = new Date(cfg.endsAt).getTime();
    if (Number.isFinite(t) && now > t) return { showing: false, detail: 'End time has passed.' };
  }
  return { showing: true, detail: 'Visitors who have not dismissed this campaign would see the modal.' };
}

export const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { showToast } = useToast();

  useEffect(() => {
    fetchSettings();
    fetchUsers();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.admin.getSettings();
      setSettings(normalizeSettingsRow(response));
    } catch (error) {
      showToast('Failed to load settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const data = await api.admin.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setSaveMessage(null);

    try {
      await api.admin.updateSettings(settings);
      showToast('Settings saved successfully', 'success');
    } catch (err) {
      showToast('Failed to save settings', 'error');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserSelect = (userId: string) => {
    const user = users.find(u => u.id === userId);
    setSelectedUser(user || null);
  };

  const handleUserUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      await api.admin.updateUser(selectedUser.id, selectedUser);
      setSaveMessage({ type: 'success', text: 'User updated successfully!' });
      await fetchUsers(); // Refresh the users list
    } catch (err) {
      setSaveMessage({ type: 'error', text: 'Failed to update user' });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUserChange = (field: keyof User, value: string | boolean) => {
    if (!selectedUser) return;
    setSelectedUser({ ...selectedUser, [field]: value });
  };

  const handleChange = (
    section: keyof AppSettings,
    field: string,
    value: string | number
  ) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSocialLinkChange = (platform: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      siteConfig: {
        ...prev.siteConfig,
        socialLinks: {
          ...prev.siteConfig.socialLinks,
          [platform]: value,
        },
      },
    }));
  };

  const handlePromoChange = <K extends keyof PromoModalConfig>(field: K, value: PromoModalConfig[K]) => {
    setSettings(prev => ({
      ...prev,
      promoModalConfig: {
        ...prev.promoModalConfig,
        [field]: value,
      },
    }));
  };

  const promoPreview = computePromoVisitorPreview(settings.promoModalConfig);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="space-y-8">
      {/* App Settings */}
      <div className="glass-panel p-6">
        <h2 className="text-xl font-semibold text-teal-300 mb-6">Application Settings</h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Site Configuration */}
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
            <h2 className="text-lg font-medium text-teal-300 mb-4">Site Configuration</h2>
            <div className="space-y-4">
              <FormField label="Site Name" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="text"
                  value={settings.siteConfig.siteName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('siteConfig', 'siteName', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Description" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <TextArea
                  value={settings.siteConfig.description}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange('siteConfig', 'description', e.target.value)}
                  rows={4}
                  required
                />
              </FormField>

              <FormField label="Contact Email" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="email"
                  value={settings.siteConfig.contactEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('siteConfig', 'contactEmail', e.target.value)}
                  required
                />
              </FormField>

              <div className="space-y-4">
                <h3 className="text-sm font-medium text-gray-900">Social Links</h3>
                <FormField label="Facebook">
                  <Input
                    type="url"
                    value={settings.siteConfig.socialLinks.facebook || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleSocialLinkChange('facebook', e.target.value)}
                  />
                </FormField>

                <FormField label="Twitter">
                  <Input
                    type="url"
                    value={settings.siteConfig.socialLinks.twitter || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleSocialLinkChange('twitter', e.target.value)}
                  />
                </FormField>

                <FormField label="Instagram">
                  <Input
                    type="url"
                    value={settings.siteConfig.socialLinks.instagram || ''}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleSocialLinkChange('instagram', e.target.value)}
                  />
                </FormField>
              </div>
            </div>
          </div>

          {/* Email Configuration */}
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
            <h2 className="text-lg font-medium text-teal-300 mb-4">Email Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="From Email" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="email"
                  value={settings.emailConfig.fromEmail}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('emailConfig', 'fromEmail', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="SMTP Host" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="text"
                  value={settings.emailConfig.smtpHost}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('emailConfig', 'smtpHost', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="SMTP Port" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="number"
                  value={settings.emailConfig.smtpPort}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('emailConfig', 'smtpPort', parseInt(e.target.value))}
                  required
                />
              </FormField>

              <FormField label="SMTP User" required className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="text"
                  value={settings.emailConfig.smtpUser}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('emailConfig', 'smtpUser', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="SMTP Password" className="bg-black/30 text-gray-200 border-teal-500/20">
                <Input
                  type="password"
                  value={settings.emailConfig.smtpPass}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('emailConfig', 'smtpPass', e.target.value)}
                />
              </FormField>
            </div>
          </div>

          {/* Payment Configuration */}
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
            <h2 className="text-lg font-medium text-teal-300 mb-4">Payment Configuration</h2>
            <div className="space-y-4">

              <FormField label="Currency" required>
                <Input
                  type="text"
                  value={settings.paymentConfig.currency}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('paymentConfig', 'currency', e.target.value)}
                  required
                />
              </FormField>
            </div>
          </div>

          {/* Trading Card Configuration */}
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-teal-500/20 p-6">
            <h2 className="text-lg font-medium text-teal-300 mb-4">Trading Card Configuration</h2>
            <div className="space-y-4">
              <FormField label="Default Style" required>
                <Input
                  type="text"
                  value={settings.tradingCardConfig.defaultStyle}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('tradingCardConfig', 'defaultStyle', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Default Font Family" required>
                <Input
                  type="text"
                  value={settings.tradingCardConfig.defaultFontFamily}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('tradingCardConfig', 'defaultFontFamily', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Default Background Color" required>
                <Input
                  type="color"
                  value={settings.tradingCardConfig.defaultBackgroundColor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('tradingCardConfig', 'defaultBackgroundColor', e.target.value)}
                  required
                />
              </FormField>

              <FormField label="Default Text Color" required>
                <Input
                  type="color"
                  value={settings.tradingCardConfig.defaultTextColor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('tradingCardConfig', 'defaultTextColor', e.target.value)}
                  required
                />
              </FormField>
            </div>
          </div>

          {/* Site promo modal (public) */}
          <div className="bg-black/20 backdrop-blur-md rounded-lg border border-amber-500/25 p-6">
            <h2 className="text-lg font-medium text-amber-200 mb-1">Site announcement modal</h2>
            <p className="text-sm text-gray-400 mb-4">
              Full-screen style promo for visitors (not shown on login or admin). Uses start/end window in the visitor&apos;s browser time. Clicking the poster opens{' '}
              <span className="text-amber-100/90">bborr.com</span> in a new tab. Image should live under{' '}
              <code className="text-amber-100/90">public/</code> (e.g. <code className="text-amber-100/90">/promo-bborr-flyer.png</code>) or an https URL.
            </p>
            <div
              className={`mb-4 rounded-md border px-3 py-2 text-sm ${
                promoPreview.showing
                  ? 'border-emerald-500/40 bg-emerald-950/30 text-emerald-200'
                  : 'border-white/10 bg-black/25 text-gray-300'
              }`}
            >
              <span className="font-semibold">{promoPreview.showing ? 'Live for visitors' : 'Not shown to visitors'}</span>
              <span className="text-gray-400"> — </span>
              {promoPreview.detail}
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="promo-enabled"
                  checked={settings.promoModalConfig.enabled}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handlePromoChange('enabled', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-500 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="promo-enabled" className="text-sm text-gray-200">
                  Enable promo modal
                </label>
              </div>
              <FormField label="Campaign key" className="bg-black/30 text-gray-200 border-amber-500/20">
                <Input
                  type="text"
                  value={settings.promoModalConfig.campaignKey}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handlePromoChange('campaignKey', e.target.value)}
                  placeholder="bborr-2026"
                />
              </FormField>
              <FormField label="Modal title (accessibility)" className="bg-black/30 text-gray-200 border-amber-500/20">
                <Input
                  type="text"
                  value={settings.promoModalConfig.title}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handlePromoChange('title', e.target.value)}
                />
              </FormField>
              <FormField label="Image URL" className="bg-black/30 text-gray-200 border-amber-500/20">
                <Input
                  type="text"
                  value={settings.promoModalConfig.imageUrl}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => handlePromoChange('imageUrl', e.target.value)}
                  placeholder="/promo-bborr-flyer.png"
                />
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Show from (optional)" className="bg-black/30 text-gray-200 border-amber-500/20">
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(settings.promoModalConfig.startsAt)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handlePromoChange('startsAt', fromDatetimeLocalValue(e.target.value))
                    }
                  />
                </FormField>
                <FormField label="Show until (optional)" className="bg-black/30 text-gray-200 border-amber-500/20">
                  <Input
                    type="datetime-local"
                    value={toDatetimeLocalValue(settings.promoModalConfig.endsAt)}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      handlePromoChange('endsAt', fromDatetimeLocalValue(e.target.value))
                    }
                  />
                </FormField>
              </div>
              <p className="text-xs text-gray-500">
                Leave start or end empty for an open-ended schedule on that side. Times use this computer&apos;s local timezone when you pick them; they are stored in UTC.
              </p>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className={`px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 ${
                isSaving ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </form>
      </div>

      {/* User Management */}
      <div className="glass-panel p-6">
        <h2 className="text-xl font-semibold text-purple-300 mb-6">User Management</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* User List */}
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium text-purple-300 mb-4">Users</h3>
            <div className="space-y-2">
              {users.map(user => (
                <button
                  key={user.id}
                  onClick={() => handleUserSelect(user.id)}
                  className={`w-full text-left p-3 rounded-md transition-colors ${
                    selectedUser?.id === user.id
                      ? 'bg-purple-900/50 text-purple-300'
                      : 'hover:bg-purple-900/30 text-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {user.avatar ? (
                      <img
                        src={user.avatar}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="w-8 h-8 text-gray-400" />
                    )}
                    <div>
                      <div className="font-medium">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-gray-400">{user.email}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* User Edit Form */}
          <div className="md:col-span-2">
            {selectedUser ? (
              <form onSubmit={handleUserUpdate} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={selectedUser.firstName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleUserChange('firstName', e.target.value)}
                      className="w-full px-4 py-2 rounded-md bg-black/30 border border-purple-500/30 text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={selectedUser.lastName}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleUserChange('lastName', e.target.value)}
                      className="w-full px-4 py-2 rounded-md bg-black/30 border border-purple-500/30 text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={selectedUser.email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleUserChange('email', e.target.value)}
                    className="w-full px-4 py-2 rounded-md bg-black/30 border border-purple-500/30 text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Bio
                  </label>
                  <textarea
                    value={selectedUser.bio || ''}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleUserChange('bio', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2 rounded-md bg-black/30 border border-purple-500/30 text-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAdmin"
                    checked={selectedUser.isAdmin}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleUserChange('isAdmin', e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-300">
                    Admin User
                  </label>
                </div>
                <button
                  type="submit"
                  className="button hover-effect"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Update User'}
                </button>
              </form>
            ) : (
              <div className="text-center text-gray-400 py-8">
                Select a user to edit their profile
              </div>
            )}
          </div>
        </div>
      </div>

      {saveMessage && (
        <div
          className={`fixed bottom-4 right-4 p-4 rounded-md ${
            saveMessage.type === 'success' ? 'bg-green-900/80 text-green-300' : 'bg-red-900/80 text-red-300'
          }`}
        >
          {saveMessage.text}
        </div>
      )}
    </div>
  );
}; 